/** Detail view for a blocked time slot, with edit and delete actions. */

import { useState } from 'react'

import {
  type BlockedSlotWithStaff,
  type ClinicId,
  type DateFormat,
  formatInstantDate,
  formatName,
  toWallClockDate,
  toWallClockTime,
} from '@snootsvet/shared'

import { FormattedDateTime, FormattedGridTimeRange } from '@/frontend/components/formatted-datetime'
import { Button } from '@/frontend/components/ui/button'
import { BlockedSlotForm } from '@/frontend/features/appointments/blocked-slot.form'
import { useDeleteBlockedSlot } from '@/frontend/features/appointments/use-delete-blocked-slot'

interface BlockedSlotDetailProps {
  block: BlockedSlotWithStaff
  /**
   * The calendar's own frame — this dialog opens from a grid slot and must agree
   * with it. Not derived from `block.clinicId`: blocked slots are fetched by time
   * range and staff, never filtered by clinic, so the block's clinic can differ
   * from the calendar's.
   */
  fmt: DateFormat
  clinicId: ClinicId
  timeZone: string
  onClose: () => void
}

export function BlockedSlotDetail({
  block,
  fmt,
  clinicId,
  timeZone,
  onClose,
}: BlockedSlotDetailProps) {
  const [isEditing, setIsEditing] = useState(false)
  const { mutate: deleteBlock, isPending, error: deleteError } = useDeleteBlockedSlot()

  const vetName = formatName(block.staff) || block.staff.email

  if (isEditing) {
    const start = new Date(block.startTime)
    const end = new Date(block.endTime)
    return (
      <BlockedSlotForm
        blockedSlotId={block.id}
        defaultValues={{
          staffId: block.staffId,
          clinicId: block.clinicId ?? clinicId,
          startDate: toWallClockDate(start, timeZone),
          startTime: toWallClockTime(start, timeZone),
          endDate: toWallClockDate(end, timeZone),
          endTime: toWallClockTime(end, timeZone),
          note: block.note ?? '',
        }}
        timeZone={timeZone}
        onSuccess={onClose}
        onCancel={() => setIsEditing(false)}
      />
    )
  }

  return (
    <>
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground w-16 shrink-0">Vet</span>
          <span>{vetName}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground w-16 shrink-0">Date</span>
          <span>{formatInstantDate(block.startTime, fmt, 'long')}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground w-16 shrink-0">Time</span>
          <span>
            <FormattedGridTimeRange start={block.startTime} end={block.endTime} fmt={fmt} />
          </span>
        </div>
        {block.note && (
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground w-16 shrink-0">Note</span>
            <span>{block.note}</span>
          </div>
        )}
        {block.createdByStaff && (
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground w-16 shrink-0">Added by</span>
            <span className="flex flex-wrap items-baseline gap-x-2">
              <span>{formatName(block.createdByStaff) || block.createdByStaff.email}</span>
              <span className="text-sm text-muted-foreground">
                <FormattedDateTime value={block.createdAt} clinicId={null} />
              </span>
            </span>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2 pt-2 border-t">
        {deleteError && <p className="w-full text-destructive-fg">{deleteError.message}</p>}
        <Button className="flex-1" onClick={() => setIsEditing(true)}>
          Edit block
        </Button>
        <Button
          variant="destructive"
          className="flex-1"
          disabled={isPending}
          onClick={() => deleteBlock(block.id, { onSuccess: onClose })}
        >
          Remove block
        </Button>
      </div>
    </>
  )
}
